import { ReactElement, useCallback, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ActionEntityType, ProcurationFeeItemsResponse, procurationFeesValidateDelete, procurationFeesCreateDeleteCr } from '@pmp/api';
import { CrudMode } from '@ohpen/enso-ui';

import { ProcurationFeesListComponent, ProcurationFeesTabProps } from '../interfaces';
import { getInitialState, reducer } from '../reducer';
import { ProcurationFeesList } from './ProcurationFeesList';
import { ProcurationFeesCrudForm } from './ProcurationFeesCrudForm';
import { ConfirmCrDeleteModal } from '@pmp/components';
import { ChangeLogDrawer, ChangeLogTarget } from '../../changeLog';

export const ProcurationFeesTab = ({ valueLists, data, isFetching, fetchData }: ProcurationFeesTabProps): ReactElement => {
  const [state, dispatch] = useReducer(reducer<ProcurationFeeItemsResponse>, getInitialState<ProcurationFeeItemsResponse>());
  const listRef = useRef<ProcurationFeesListComponent | null>(null);
  const [changeLogTarget, setChangeLogTarget] = useState<ChangeLogTarget | null>(null);
  const { t } = useTranslation();

  const onCreate = useCallback(() => {
    dispatch({ type: 'openCrud', record: null, mode: CrudMode.CREATE });
  }, []);

  const onView = useCallback((record: ProcurationFeeItemsResponse) => {
    dispatch({ type: 'openCrud', record, mode: CrudMode.VIEW });
  }, []);

  const onEdit = useCallback((record: ProcurationFeeItemsResponse) => {
    dispatch({ type: 'openCrud', record, mode: CrudMode.EDIT });
  }, []);

  const onCloseCrud = useCallback(() => {
    dispatch({ type: 'closeCrud' });
  }, []);

  const onViewChangeLog = useCallback((record: ProcurationFeeItemsResponse) => {
    setChangeLogTarget({ entityType: ActionEntityType.PROCURATION_FEES, id: record.id, name: record.name });
  }, []);

  const onCloseChangeLog = useCallback(() => {
    setChangeLogTarget(null);
  }, []);

  const onDelete = useCallback((record: ProcurationFeeItemsResponse) => {
    dispatch({ type: 'openDeleteModal', records: [record] });
  }, []);

  const onBulkDelete = useCallback((records: ProcurationFeeItemsResponse[]) => {
    dispatch({ type: 'openDeleteModal', records });
  }, []);

  const onCloseDeleteModal = useCallback(() => {
    dispatch({ type: 'closeDeleteModal' });
  }, []);

  const onDeleteChangeRequestCreated = useCallback(() => {
    listRef.current?.resetSelectedRows();
    fetchData();
  }, [fetchData]);

  return (
    <>
      <ProcurationFeesList
        ref={listRef}
        data={data ?? []}
        isFetching={isFetching}
        valueLists={valueLists}
        onCreate={onCreate}
        onView={onView}
        onEdit={onEdit}
        onDelete={onDelete}
        onViewChangeLog={onViewChangeLog}
        onBulkDelete={onBulkDelete}
      />
      <ChangeLogDrawer target={changeLogTarget} onClose={onCloseChangeLog} />
      {state.crud.open && state.crud.mode && (
        <ProcurationFeesCrudForm
          mode={state.crud.mode}
          record={state.crud.record}
          valueLists={valueLists}
          refreshTable={fetchData}
          onClose={onCloseCrud}
        />
      )}
      <ConfirmCrDeleteModal
        open={state.deleteModal.open}
        records={state.deleteModal.records}
        idKey="id"
        nameKey="name"
        entityLabel={t('lender.procurationFees.labels.procurationFee')}
        entityLabelPlural={t('lender.procurationFees.labels.procurationFees')}
        getPrecheck={procurationFeesValidateDelete}
        createDeleteChangeRequest={procurationFeesCreateDeleteCr}
        refreshTable={onDeleteChangeRequestCreated}
        onClose={onCloseDeleteModal}
      />
    </>
  );
};
